/**
 * threadListIdentity — the identity facts every thread LIST surface reads
 * (chat-thread experience PR-5, 2026-09-09): who is in a thread, what people
 * are called, and the viewer's own categories (names are personal — no
 * registry name rides here, 2026-09-09). One props bundle + two resolvers, so the rail, the chip
 * strip and the mobile sheet name and file a thread identically.
 */
import type { ChatThread } from './types';
import type { ThreadCategory, ThreadListLang } from '../../types/threadCategories';
import { defaultThreadTitle } from '../../services/groupThreads/canonicalThreadTitle';
import {
  resolveThreadDisplayKind,
  resolveThreadDisplayTitle,
  type ThreadDisplayKind,
} from '../../services/groupThreads/threadDisplayTitle';

export interface ThreadListIdentityProps {
  /**
   * threadId → registered participant uids, from the SAME membership read
   * that feeds `participantsByThread` (GlobalChatSystem's
   * `sharedThreadParticipantIds`). Names the other person in a direct
   * message; absent ⇒ no thread is a direct message (counts still section
   * group threads).
   */
  participantIdsByThread?: Record<string, string[]>;
  /** uid → display name (the viewer's roster, then server-resolved names). */
  memberLabels?: Record<string, string>;
  /** The viewer's personal categories (order = display order). */
  categories?: readonly ThreadCategory[] | null;
  /** The viewer's language — names the placeholder an unnamed thread wears. */
  lang?: ThreadListLang;
}

export function threadDisplayKindFor(
  thread: Pick<ChatThread, 'id' | 'sharedThread'>,
  identity: ThreadListIdentityProps | null | undefined,
  viewerUid: string | null | undefined,
  participantsByThread?: Record<string, number> | null,
): ThreadDisplayKind {
  return resolveThreadDisplayKind({
    participantIds: identity?.participantIdsByThread?.[thread.id] ?? null,
    participantCount: participantsByThread?.[thread.id] ?? null,
    selfId: viewerUid,
    sharedThread: thread.sharedThread === true,
  });
}

export function threadDisplayTitleFor(
  thread: Pick<ChatThread, 'id' | 'title' | 'userRenamed'>,
  identity: ThreadListIdentityProps | null | undefined,
  viewerUid: string | null | undefined,
  routineName?: string | null,
): string {
  return resolveThreadDisplayTitle({
    thread,
    selfId: viewerUid,
    participantIds: identity?.participantIdsByThread?.[thread.id] ?? null,
    memberLabels: identity?.memberLabels ?? null,
    routineName: routineName ?? null,
    // NOT `thread.title`: that is what made one thread read "New thread" to the
    // person who started it and "Group thread" to everybody added to it
    // (2026-09-13). The placeholder is the viewer's own, and the same one for
    // every thread that has no name yet.
    fallback: defaultThreadTitle(identity?.lang),
  });
}
